import { plural } from './format';
import type { Messages } from './en';

/**
 * Spanish, for the readers docs/i18n-design.md §1 actually names: *"roughly a
 * sixth of households speak Spanish (Puerto Rican and Dominican communities)"*.
 * On this surface they are not guests — they are the therapist, the cook, the
 * captain and the person on the desk, and this is the app they work in all day.
 *
 * ## The register, and why it is not the guest site's
 *
 * **Neutral Latin American Spanish, `usted` throughout**, matching
 * `apps/site/src/messages/es.ts` word for word wherever the same concept
 * appears: `celular`, `boleto`, `habitación`, `huésped`, `reservar`. Never
 * Peninsular — no `móvil`, `coger`, `vale`, `ordenador`, and no `vosotros`,
 * which nobody in the Caribbean uses.
 *
 * What differs from the guest site is the *tone*, and deliberately. That page is
 * talking a stranger into a booking; this is a till a colleague uses two hundred
 * times a shift. So: **brisk, concrete, never chatty.** Buttons are verbs
 * (`Cobrar`, `Emitir`, `Reservar`), status words are short (`por enviar`,
 * `sin cita`), and nothing apologizes. `usted` stays because it is the neutral
 * commercial register across both communities, not because the app is being
 * formal with the person holding it.
 *
 * ## Where a word had to be chosen rather than translated
 *
 *  - **`cliente` at the till, `huésped` in lodging.** The English says "guest"
 *    for both, and Spanish does not: the person on a massage table is a
 *    `cliente`, and `huésped` means someone with a room. Front desk uses
 *    `huésped`; checkout, clients and the book use `cliente`.
 *  - **`cortesía`, not `regalo`, for gift credit.** §7.4 rule 2's gift credit is
 *    goodwill the shop extends, never a present somebody bought — and the *gift
 *    certificate*, which somebody really did buy, is `certificado de regalo`.
 *    Using one word for both would collapse the exact distinction the two pools
 *    exist to keep apart.
 *  - **`sin cita`, not `walk-in`.** The loanword is common on menus in the
 *    territory but has no plural and no adjective form, and this catalog needs
 *    both.
 *
 * ## What is asserted about this file
 *
 * The declaration below is `: Messages`, and that is the whole safety story: a
 * key this file forgets is a **build failure**, and a template whose parameters
 * drift from the English one is a build failure. There is no runtime fallback to
 * English anywhere, on purpose — a fallback is a way for half a till to be in
 * the wrong language without anybody finding out.
 */

export const es: Messages = {
  /* ---------------------------------------------------------------- */
  /* Comunes                                                           */
  /* ---------------------------------------------------------------- */

  'common.add': 'Agregar',
  'common.cancel': 'Cancelar',
  'common.close': 'Cerrar',
  'common.edit': 'Editar',
  'common.name': 'Nombre',
  'common.notes': 'Notas',
  'common.open': 'Abrir',
  'common.price': 'Precio',
  'common.remove': 'Quitar',
  'common.restore': 'Restaurar',
  'common.retire': 'Retirar',
  'common.save': 'Guardar',

  /* ---------------------------------------------------------------- */
  /* El armazón                                                        */
  /* ---------------------------------------------------------------- */

  'tabs.today': 'Hoy',
  'tabs.calendar': 'Calendario',
  'tabs.orders': 'Pedidos',
  'tabs.reservations': 'Reservas',
  'tabs.departures': 'Salidas',
  'tabs.frontdesk': 'Recepción',
  'tabs.albums': 'Álbumes',
  'tabs.clients': 'Clientes',
  'tabs.giftcards': 'Tarjetas de regalo',
  'tabs.tasks': 'Tareas',
  'tabs.reports': 'Informes',
  'tabs.settings': 'Ajustes',
  'tabs.parcels': 'Paquetes',
  'tabs.newsletter': 'Boletín',
  'tabs.team': 'Equipo',
  'tabs.navLabel': 'Secciones',

  /* ---------------------------------------------------------------- */
  /* La barra de estado                                                */
  /* ---------------------------------------------------------------- */

  'status.online': 'En línea',
  'status.reconnecting': 'Reconectando',
  'status.offline': 'Sin conexión',
  'status.notLiveTitle': 'Puede que otro sistema todavía esté tomando reservas.',
  'status.waitingToSend': ({ count }) => `${String(count)} por enviar`,
  'status.needsYou': ({ count }) =>
    `${String(count)} ${plural(count, 'necesita su atención', 'necesitan su atención')}`,
  'status.signOut': 'Cerrar sesión',
  /* La vista de soporte de la consola ("ver lo que ella ve"; `auth/support.ts`). */
  'status.supportBanner': ({ operator, until }) =>
    `Vista de soporte — solo lectura — operador ${operator} hasta ${until}`,
  'status.supportEnd': 'Terminar vista de soporte',
  'status.supportEnded': 'La vista de soporte terminó',
  'status.supportEndedHint':
    'Quince minutos es todo. Abra otra vista desde la consola; cada una es su propia fila de auditoría.',
  'actions.supportRefused':
    'Esta es una vista de soporte: puede ver el libro pero no cambiarlo. No se puso nada en cola.',
  'actions.readOnlyRefused':
    'Este puesto puede leer el libro pero no cambiarlo. No se puso nada en cola.',

  /* ---------------------------------------------------------------- */
  /* Inicio de sesión                                                  */
  /* ---------------------------------------------------------------- */

  'signIn.devLead':
    'Inicio de sesión de desarrollo. Cualquier correo funciona y el token no está firmado — esta compilación solo habla con un ',
  'signIn.devMid': ' iniciado con ',
  'signIn.errorUnavailable':
    'No se pudo alcanzar el servicio de inicio de sesión. Revise la conexión.',
  'signIn.errorMismatch': 'Ese correo y esa contraseña no coinciden.',
  'signIn.email': 'Correo electrónico',
  'signIn.password': 'Contraseña',
  'signIn.signingIn': 'Iniciando sesión…',
  'signIn.submit': 'Iniciar sesión',

  /* ---------------------------------------------------------------- */
  /* La personalidad                                                   */
  /* ---------------------------------------------------------------- */

  // The heart keeps its heart. `el botón +` reads as naturally in Spanish as
  // "the + button" does in English, and both name something on the screen.
  'brand.heartButton': 'el botón del corazón',
  'brand.plusButton': 'el botón +',

  /* ---------------------------------------------------------------- */
  /* De dónde vino una reserva                                         */
  /* ---------------------------------------------------------------- */

  'source.partner': 'conserje',
  'source.walkIn': 'sin cita',
  'source.guestWeb': 'sitio web',
  'source.import': 'importado',
  'source.counter': 'mostrador',

  /* ---------------------------------------------------------------- */
  /* Antigüedad de los datos (§8.1)                                    */
  /* ---------------------------------------------------------------- */

  'time.neverSynced': 'nunca sincronizado',
  'time.syncedJustNow': 'sincronizado hace un momento',
  'time.lastSyncedMinutes': ({ clock, minutes }) =>
    `última sincronización ${clock} · hace ${String(minutes)} min`,
  'time.lastSyncedHours': ({ clock, hours }) =>
    `última sincronización ${clock} · hace ${String(hours)} h`,
  'time.notReadYet': 'todavía sin leer',
  'time.readJustNow': 'leído hace un momento',
  'time.readMinutes': ({ clock, minutes }) => `leído ${clock} · hace ${String(minutes)} min`,
  'time.readHours': ({ clock, hours }) => `leído ${clock} · hace ${String(hours)} h`,

  // No `toUpperCase()`. CLDR renders the Spanish marker as `p. m.` — lower case,
  // with full stops — and that is how a Spanish clock is written.
  'time.clockOnHour': ({ hour, meridiem }) => `${hour} ${meridiem}`,
  'time.clockWithMinutes': ({ hour, minute, meridiem }) => `${hour}:${minute} ${meridiem}`,

  /* ---------------------------------------------------------------- */
  /* Hoy                                                               */
  /* ---------------------------------------------------------------- */

  'today.quickSale': 'Venta rápida',
  'today.ringUpPickup': 'Cobrar el pedido',
  'today.walkIn': 'Sin cita',
  'today.empty': 'No hay nada en la agenda para hoy.',
  'today.emptyHeart': ({ button }) => ` Empiece una con ${button}.`,
  'today.duration': ({ minutes }) => `${String(minutes)} min`,
  'today.waitingToSend': 'por enviar',
  'today.checkoutWaiting': 'cobro por enviar',
  'today.closeOut': 'Cerrar la cuenta',
  'today.paymentLink': 'Enlace de pago',
  'today.done': 'Listo',
  'today.noShow': 'No llegó',
  'today.cancel': 'Cancelar',

  /* Cumpleaños (B2). Una línea, armada con las dos cláusulas de abajo. */
  'today.birthdays': ({ list }) => `Cumpleaños: ${list}`,
  'today.birthdaysToday': ({ names }) => `hoy — ${names}`,
  'today.birthdaysTomorrow': ({ names }) => `mañana — ${names}`,

  /* Lo cobrado hoy. La cláusula de espera es un conteo, nunca dinero. */
  'today.takings': ({ total }) => `En el mostrador hoy: ${total}`,
  'today.takingsTender': ({ tender, amount }) => `${tender} ${amount}`,
  'today.takingsNone': 'Todavía no se ha cobrado nada hoy',
  'today.takingsWaiting': ({ count }) =>
    `+ ${String(count)} ${plural(count, 'venta', 'ventas')} por enviar`,
  'today.takingsAsOf': ({ clock }) => `actualizado ${clock}`,

  /* ---------------------------------------------------------------- */
  /* Solicitudes del sitio (docs/lovespa-discovery.md §2.3)            */
  /* ---------------------------------------------------------------- */

  'requests.unverified': 'sin verificar',
  'requests.noName': 'Alguien',
  'requests.book': 'Reservar esto',
  'requests.appointmentsTitle': 'Solicitudes de cita',
  'requests.verified': 'número verificado',
  'requests.new': 'clienta nueva',
  'requests.holdsUntil': ({ when }: { when: string }) => `Reservada hasta ${when}`,
  'requests.confirm': 'Confirmar',
  'requests.decline': 'Rechazar',
  'requests.propose': 'Otra hora',
  'requests.andTrust': 'y que pueda reservar sola la próxima vez',
  'requests.declineMessage': 'Una nota para ella (opcional)',
  'requests.proposeStart': 'Ofrecer esta hora',
  'requests.send': 'Enviar',
  'requests.cancel': 'Volver',
  'requests.proposed': 'Esperando su respuesta',
  'requests.noticeQueued': 'Mensaje enviado',
  'requests.noticeNoConsent': 'Sin mensaje — no ha aceptado recibir textos',
  'requests.noticeNoPhone': 'Sin mensaje — no hay número',
  'requests.inquiriesTitle': 'Mensajes',
  'requests.answered': 'Contestado',
  'requests.markAnswered': 'Marcar contestado',
  'requests.answeredAt': ({ when }) => `Contestado ${when}`,
  'requests.openCount': ({ count }) =>
    count === 1 ? '1 mensaje por contestar' : `${String(count)} mensajes por contestar`,
  'requests.tentative': 'Solicitada',

  /* ---------------------------------------------------------------- */
  /* Calendario                                                        */
  /* ---------------------------------------------------------------- */

  'calendar.closedToday': 'Cerrado este día.',
  'calendar.noHours': 'Todavía no hay horario para este día — vea Ajustes.',
  'calendar.bookAt': ({ resource, day }) => `Reservar ${resource} el ${day}`,
  'calendar.bookOn': ({ day }) => `Reservar una hora el ${day}`,

  'calendar.previous': 'Antes',
  'calendar.next': 'Después',
  'calendar.pickDay': 'Ir a una fecha',
  'calendar.today': 'Hoy',
  'calendar.viewLabel': 'Cuánto mostrar',
  'calendar.dayView': 'Día',
  'calendar.weekView': 'Semana',
  'calendar.whichResource': 'Qué sala o persona',
  'calendar.allResources': 'Todos',
  'calendar.deskMode': 'Pantalla del mostrador',
  'calendar.exitDesk': 'Salir de la pantalla del mostrador',
  'calendar.closed': 'Cerrado',

  'calendar.moveHint': ({ minutes }) =>
    `Arrastre una cita para moverla — o selecciónela y use las flechas (${String(minutes)} min por paso), luego Enter para confirmar.`,

  'calendar.blockLabel': ({ name, service, clock, minutes }) =>
    `${name}, ${service}, ${clock}, ${String(minutes)} min`,
  'calendar.gapAfter': ({ minutes }) => `${String(minutes)} min reservados después de esta`,
  'calendar.companionLabel': ({ name, service, clock }) =>
    `${name}, ${service}, ${clock} — esta sala está ocupada con ello`,
  'calendar.ghostClash': ({ name, clock }) => `Choca con ${name} a las ${clock}`,

  'calendar.moveDialog': 'Mover esta cita',
  'calendar.moveTitle': '¿Moverla?',
  'calendar.moveQuestion': ({ name, service, when }) => `¿Mover ${service} de ${name} al ${when}?`,
  'calendar.moveNotTexted': ({ minutes }) =>
    `Siguen siendo ${String(minutes)} min. No se le envía ningún mensaje por el cambio — ese le toca a usted.`,
  'calendar.moveClash': ({ name, clock }) =>
    `Este dispositivo ya tiene a ${name} a las ${clock} en esa sala. Decide la nube; si lo rechaza, le llegará una tarea.`,
  'calendar.move': 'Moverla',

  'calendar.proposeTitle': '¿Ofrecer otra hora?',
  'calendar.proposeQuestion': ({ name, service, when }) =>
    `¿Ofrecerle a ${name} el ${when} para su ${service}?`,
  'calendar.proposeTexts':
    'Sigue siendo una solicitud hasta que ella acepte, y se le envía la nueva hora por mensaje.',
  'calendar.propose': 'Ofrecerla',

  'calendar.lengthLabel': ({ name, minutes }) =>
    `Cambiar cuánto dura ${name} — ahora ${String(minutes)} min`,
  'calendar.ghostLength': ({ minutes }) => `${String(minutes)} min`,
  'calendar.ghostLengthPriced': ({ minutes, price }) => `${String(minutes)} min · ${price}`,
  'calendar.lengthOptionNote': ({ was, option, price }) =>
    `Duraba ${String(was)} min. La hora de inicio no cambia. Ahora se vende como «${option}», a ${price} — el precio de su menú.`,
  'calendar.lengthTitle': '¿Cambiar la duración?',
  'calendar.lengthQuestion': ({ name, service, minutes }) =>
    `¿Dejar ${service} de ${name} en ${String(minutes)} min?`,
  'calendar.lengthNote': ({ was }) =>
    `Duraba ${String(was)} min. La hora de inicio no cambia, y el precio tampoco — cobre el tiempo extra en la caja si quiere.`,
  'calendar.lengthApply': 'Cambiarla',

  /* ---------------------------------------------------------------- */
  /* Tareas (§8.3)                                                     */
  /* ---------------------------------------------------------------- */

  'tasks.nothingTitle': 'Nada requiere su atención',
  'tasks.nothingBody':
    'Todo lo que se le indicó a este iPad llegó a la nube. Lo que choque mientras esté sin conexión aparecerá aquí.',
  'tasks.heading': ({ count }) =>
    `${String(count)} ${plural(count, 'cosa requiere su atención', 'cosas requieren su atención')}`,
  'tasks.findAnother': 'Buscar otro horario',
  'tasks.dismiss': 'Descartar',
  'tasks.didNotGoThrough': ({ what }) => `${what} no se pudo completar`,

  'tasks.data.deleteTitle': ({ name }) => `${name} pidió que le olviden`,
  'tasks.data.exportTitle': ({ name }) => `${name} pidió una copia de sus datos`,
  'tasks.data.asked': ({ clock, days }) =>
    days < 0
      ? `Solicitado a las ${clock} — ${String(-days)} ${plural(-days, 'día', 'días')} de retraso.`
      : `Solicitado a las ${clock} — quedan ${String(days)} ${plural(days, 'día', 'días')} para responder.`,
  'tasks.data.deleteBody':
    'Al ejecutarlo se borran su nombre, teléfono, correo, notas y preferencias. Las reservas y las ventas quedan en los libros sin nombre. No se puede deshacer.',
  'tasks.data.exportBody':
    'Al ejecutarlo se prepara un archivo con todo lo que se guarda sobre esta persona, para entregárselo. No se envía por correo.',
  'tasks.data.runDelete': 'Olvidar a este cliente',
  'tasks.data.runExport': 'Preparar el archivo',
  'tasks.data.ownerOnly': 'Solo el propietario puede ejecutarlo.',
  'tasks.data.forgotten': ({ identities, preferences }) =>
    `Hecho. Se borraron ${String(identities)} ${plural(identities, 'identidad', 'identidades')} y ${String(preferences)} ${plural(preferences, 'preferencia', 'preferencias')}; el libro no se tocó.`,
  'tasks.data.exported': ({ name }) =>
    `El archivo de ${name} está listo. Guárdelo y entréguelo; aquí no se conserva.`,
  'tasks.data.saveFile': 'Guardar el archivo',

  // The `who` clause moves: English hangs "for Marisol Vega" off the verb,
  // Spanish puts it after the person who took the slot or drops it entirely.
  // That is exactly why each opening is one entry rather than a frame plus a
  // pre-built tail.
  'task.subjectThatTime': 'Ese horario',
  'task.subjectThisTime': ({ when }) => `Esta cita de las ${when}`,
  'task.takenBySomeone': ({ subject }) => `${subject} se ocupó mientras estaba sin conexión.`,
  'task.takenByPartner': ({ subject, who }) =>
    who === null
      ? `${subject} la reservó el conserje mientras estaba sin conexión.`
      : `${subject} la reservó el conserje para ${who} mientras estaba sin conexión.`,
  'task.takenByWalkIn': ({ subject, who }) =>
    who === null
      ? `${subject} se le dio a alguien sin cita mientras estaba sin conexión.`
      : `${subject} se le dio sin cita a ${who} mientras estaba sin conexión.`,
  'task.takenByImport': ({ subject, who }) =>
    who === null
      ? `${subject} ya estaba en la agenda importada mientras estaba sin conexión.`
      : `${subject} ya estaba en la agenda importada a nombre de ${who} mientras estaba sin conexión.`,
  'task.takenByGuestWeb': ({ subject, who }) =>
    who === null
      ? `${subject} se reservó por el sitio web mientras estaba sin conexión.`
      : `${subject} se reservó por el sitio web a nombre de ${who} mientras estaba sin conexión.`,
  'task.takenByStaff': ({ subject, who }) =>
    who === null
      ? `${subject} se reservó en el mostrador mientras estaba sin conexión.`
      : `${subject} se reservó en el mostrador a nombre de ${who} mientras estaba sin conexión.`,
  'task.askPickAnother': '¿Elegir otro horario?',
  'task.askRebook': ({ person }) => `¿Reservar de nuevo a ${person}?`,
  'task.askReschedule': ({ person }) => `¿Reprogramar a ${person}?`,

  'task.plainUnknown': 'Esto no se pudo completar.',
  'task.plainNotFound': ({ message }) =>
    `${message} Puede que se haya cancelado o eliminado mientras estaba sin conexión.`,
  'task.plainForbidden': ({ message }) =>
    `${message} Este dispositivo tiene la sesión iniciada en un negocio distinto al de este cambio.`,
  'task.plainUnsupported': ({ message }) =>
    `${message} Esa parte de la app está apagada para este negocio.`,

  /* ---------------------------------------------------------------- */
  /* Las intenciones, como las describe la lista de tareas             */
  /* ---------------------------------------------------------------- */

  'action.book': ({ client, service }) => `${client} — ${service}`,
  'action.reschedule': ({ client, service }) => `Moviendo a ${client} — ${service}`,
  'action.retime': ({ client, service, minutes }) =>
    `Dejando ${service} de ${client} en ${String(minutes)} min`,
  'action.cancel': ({ name }) => `Cancelando a ${name}`,
  'action.markDone': ({ name }) => `Marcando a ${name} como atendido`,
  'action.markNoShow': ({ name }) => `Marcando que ${name} no llegó`,
  'action.addClient': ({ name }) => `Agregando a ${name}`,
  'action.editClient': ({ name }) => `Editando a ${name}`,
  'action.archiveClient': ({ name }) => `Archivando a ${name}`,
  'action.restoreClient': ({ name }) => `Restaurando a ${name}`,
  'action.addContact': ({ name }) => `Agregando un contacto de ${name}`,
  'action.removeContact': ({ name }) => `Quitando un contacto de ${name}`,
  'action.notePreference': ({ label, name }) => `Anotando "${label}" para ${name}`,
  'action.rememberDuration': ({ minutes, name, service }) =>
    `Recordando ${String(minutes)} min para ${service} de ${name}`,
  'action.forgetDuration': ({ name, service }) =>
    `Volviendo al tiempo de siempre para ${service} de ${name}`,
  'action.removePreference': ({ label, name }) => `Quitando "${label}" de ${name}`,
  'action.addService': ({ name }) => `Agregando ${name} al menú`,
  'action.editService': ({ name }) => `Editando ${name}`,
  /* S2 — el equipo, tal como lo nombraría la lista de Tareas. */
  'action.addPerson': ({ name }) => `Agregando a ${name} al equipo`,
  'action.addRoom': ({ name }) => `Agregando ${name}`,
  'action.editPerson': ({ name }) => `Editando a ${name}`,
  'action.timeOff': ({ name }) => `Tiempo libre para ${name}`,
  'action.setHours': 'Cambiando el horario de atención',
  'action.closeCalendar': 'Cerrando el calendario',
  'action.closeFor': ({ reason }) => `Cerrando por ${reason}`,
  'action.reopen': ({ what }) => `Reabriendo ${what}`,
  'action.theCalendar': 'el calendario',
  'action.quickSale': ({ name }) => `Venta rápida — ${name}`,
  'action.walkIn': 'sin cita',
  'action.checkout': ({ name }) => `Cobro — ${name}`,
  'action.guest': 'cliente',
  'action.collectReservation': ({ name }) => `Pedido recogido — ${name}`,
  'action.bookStay': ({ client, room }) => `Estadía para ${client} — ${room}`,
  'action.sellSeat': ({ client, trip }) => `Asiento para ${client} — ${trip}`,
  'action.addCredit': ({ name }) => `Agregando crédito a ${name}`,
  'action.giveCredit': ({ name }) => `Dando cortesía a ${name}`,
  'action.addProduct': ({ name }) => `Agregando ${name} al estante`,
  'action.editProduct': ({ name }) => `Editando ${name}`,

  /* ---------------------------------------------------------------- */
  /* Clientes                                                          */
  /* ---------------------------------------------------------------- */

  'clients.search': 'Buscar clientes',
  'clients.new': 'Nuevo',
  'clients.noneMatch': 'No coincide nadie.',
  'clients.noneMatchHeart': ({ button }) => ` Agregue a alguien con ${button}.`,
  'clients.noContact': 'sin contacto',
  'clients.visits': ({ count }) => `${String(count)} ${plural(count, 'visita', 'visitas')}`,
  'clients.archived': 'archivado',
  'clients.pickSomeone': 'Elija a alguien.',
  'clients.archive': 'Archivar',
  'clients.contactHeading': 'Contacto',
  'clients.noIdentity':
    'Sin teléfono ni correo — no se puede reservar para este cliente hasta agregar uno.',
  'clients.preferencesHeading': 'Preferencias y alergias',
  'clients.categoryPreference': 'preferencia',
  'clients.categoryAllergy': 'alergia',
  'clients.staffOnly': 'solo personal',
  'clients.staffOnlyTitle': 'Nunca se le muestra al agente de reservas.',
  'clients.nothingNoted': 'Todavía no hay nada anotado.',
  'clients.addPreference': 'Agregar una preferencia o alergia',
  'clients.kindPhone': 'teléfono',
  'clients.kindEmail': 'correo',
  'clients.addIdentity': 'Agregar un teléfono o correo',
  'clients.newClient': 'Cliente nuevo',
  'clients.phone': 'Teléfono',

  /* Sus tiempos (A1) — cuánto tarda cada servicio con esta clienta. */
  'clients.lengthsHeading': 'Sus tiempos',
  'clients.lengthOption': ({ service, option }) => `${service} · ${option}`,
  'clients.lengthHers': ({ minutes, catalog }) =>
    `${String(minutes)} min · normalmente ${String(catalog)} min`,
  'clients.lengthForgotten': ({ catalog }) => `vuelve a los ${String(catalog)} min de siempre`,
  'clients.lengthForget': 'Olvidar',
  'clients.lengthsNone': 'Nada distinto — cada servicio tarda lo de siempre.',
  'clients.lengthsNote':
    'Un tiempo aquí cambia lo que se ofrece de ahora en adelante. Las citas ya reservadas conservan el tiempo con el que se reservaron.',
  'clients.lengthAddService': 'Agregar un tiempo para…',
  'clients.lengthWhich': 'Cuál duración',
  'clients.lengthHowever': 'Como sea que se venda',
  'clients.lengthMinutes': 'Minutos',

  /* Lo que ha comprado (B3). */
  'clients.purchasesHeading': 'Compras',
  'clients.purchasesLoading': 'Buscando…',
  'clients.purchasesOffline': 'Las compras necesitan conexión. Esto no está en la copia local.',
  'clients.purchasesStale': 'Sin conexión — esta es la última lista que llegó.',
  'clients.purchasesNone': 'Todavía no ha comprado nada.',
  'clients.purchasesMore': 'Ver más',
  /* Refunds (migration 0046). */
  'clients.refund': 'Reembolsar',
  'clients.purchaseTip': ({ amount }: { amount: string }) => `propina ${amount}`,
  'clients.purchaseRefunded': ({ amount }: { amount: string }) => `${amount} reembolsado`,
  'refund.dialogLabel': 'Reembolso',
  'refund.title': 'Devolver dinero',
  'refund.loading': 'Consultando lo que queda de esta venta…',
  'refund.needsConnection':
    'Un reembolso necesita conexión: solo el servidor sabe lo que ya se devolvió.',
  'refund.sale': ({ date, total, left }: { date: string; total: string; left: string }) =>
    `${date} · ${total} · quedan ${left} por reembolsar`,
  'refund.amount': 'Importe',
  'refund.amountHint': 'Déjalo en blanco para devolver todo lo que queda.',
  'refund.tip': ({ left }: { left: string }) => `Propina a devolver (quedan ${left})`,
  'refund.howBack': '¿Cómo se devolvió?',
  'refund.recordedOnly': 'Solo se anota: esta aplicación no procesa ningún reembolso.',
  'refund.tender.cash': 'efectivo',
  'refund.tender.check': 'cheque',
  'refund.tender.venmo': 'Venmo',
  'refund.tender.paypal': 'PayPal',
  'refund.tender.card': 'tarjeta',
  'refund.tender.other': 'otro',
  'refund.tender.credit_cash': 'saldo de la cuenta',
  'refund.tender.credit_gift': 'saldo de cortesía',
  'refund.labelLabel': '¿Qué fue?',
  'refund.labelPlaceholder': 'Zelle, transferencia…',
  'refund.restock': 'Vuelve al estante',
  'refund.restockHint': 'Marca esto solo si los productos se pueden vender otra vez.',
  'refund.reason': 'Motivo',
  'refund.tooMuch': 'Eso es más de lo que queda por devolver de esta venta.',
  'refund.give': 'Anotar el reembolso',
  'refund.giving': 'Anotando…',
  'refund.failed': 'Ese reembolso no se anotó.',
  'refund.alreadyBack': ({ amount }: { amount: string }) => `${amount} ya reembolsado`,
  /* The tips report (migration 0046). */
  'reports.tips': 'Propinas',
  'reports.tipsCollected': ({ amount }: { amount: string }) => `${amount} cobradas`,
  'reports.tipsRefunded': ({ amount }: { amount: string }) => `${amount} devueltas`,
  'reports.tipsUnattributed': ({ amount }: { amount: string }) => `${amount} sin asignar`,
  'reports.tipsNobody': 'No hay propinas en este periodo.',
  'reports.taxTile': 'Impuestos cobrados',
  'reports.refundsTile': 'Reembolsado',
  'reports.taxCollected': ({ amount }: { amount: string }) => `${amount} de impuestos cobrados`,
  'reports.refundedOut': ({ amount, n }: { amount: string; n: number }) =>
    `${amount} reembolsados en ${String(n)}`,

  /* ---------------------------------------------------------------- */
  /* Permisos — el grupo (ui/Permissions.tsx)                          */
  /*                                                                   */
  /* El mismo verbo en los tres, en imperativo hacia quien tiene el    */
  /* iPad y con `usted` por detrás, como el resto de esta pantalla.    */
  /* `boletín` lleva tilde: el bloque del boletín se escribió sin      */
  /* tildes y eso era un descuido, no una convención.                  */
  /* ---------------------------------------------------------------- */

  'permissions.heading': 'Permisos',
  'permissions.note': 'Lo que ella ha aceptado, y lo que usted le ha permitido hacer.',

  'permissions.newsletterTick': 'Enviarle nuestro boletín por correo',
  'permissions.newsletterOff': 'Desactivado — no recibe el boletín.',
  'permissions.newsletterSince': ({ day, how }) => `Desde el ${day} — ${how}`,
  'permissions.newsletterSinceUnknown':
    'Activado desde antes de que empezáramos a registrar cuándo y cómo.',
  'permissions.noEmail':
    'Sin correo electrónico — no se le puede enviar el boletín hasta que agregue uno.',
  'permissions.emailHowStaff': 'usted lo marcó',
  'permissions.emailHowGuestSite': 'se suscribió en el sitio web',
  'permissions.emailHowUnsubscribe': 'usó el enlace para darse de baja',
  'permissions.emailHowBounce': 'su buzón dejó de aceptar correo',
  'permissions.emailHowImport': 'llegó con una importación, que no registró nada',
  'permissions.emailHowAnonymized': 'el cliente pidió que le olvidaran y se ejecutó la solicitud',

  'permissions.bookOnlineTick': 'Permitirle reservar sola por internet',
  'permissions.bookOnlineNote':
    'Solo importa cuando su sitio web les pide a las personas nuevas que soliciten una hora — entonces ella reserva en lugar de pedir. No registramos cuándo se le dio este permiso.',

  /* ---------------------------------------------------------------- */
  /* Consentimiento para recibir mensajes (§2.6.1(a), migración 0041)  */
  /* ---------------------------------------------------------------- */

  /**
   * `sms.script` es lo que la persona del mostrador **dice en voz alta**, así
   * que se tradujo como se pregunta en el territorio — `usted`, directo, sin
   * rodeos — y no como se traduciría un aviso legal. `mensaje` (nunca `SMS`,
   * que aquí nadie dice hablando) por la misma razón que `celular` y no
   * `móvil`.
   */
  'sms.heading': 'Recordatorios',
  'sms.tick': 'Enviarle recordatorios de sus citas por mensaje',
  'sms.ask': 'Pregúntele:',
  'sms.script': '¿Está bien si le enviamos un recordatorio la noche anterior?',
  'sms.off': 'Desactivado — no recibe recordatorios.',
  'sms.since': ({ day, how }) => `Desde el ${day} — ${how}`,
  'sms.howStaff': 'usted lo marcó',
  'sms.howGuestSite': 'lo pidió en el sitio web',
  'sms.howReplyYes': 'respondió YES por mensaje',
  'sms.howReplyStop': 'respondió STOP',
  'sms.howImport': 'llegó con una importación, que no registró nada',
  'sms.howAnonymized': 'el cliente pidió que le olvidaran y se ejecutó la solicitud',
  'sms.sinceUnknown': 'Activado desde antes de que empezáramos a registrar cuándo y cómo.',
  'sms.noPhone': 'Sin número de teléfono — no se le puede escribir hasta que agregue uno.',

  /* ---------------------------------------------------------------- */
  /* Crédito en cuenta (§7.4 regla 2)                                  */
  /* ---------------------------------------------------------------- */

  'credit.heading': 'En cuenta',
  'credit.cash': 'Crédito en efectivo',
  'credit.cashHint': ' — dinero pagado; un pago cuando se usa',
  'credit.gift': 'Crédito de cortesía',
  'credit.giftHint': ' — cortesía; un descuento cuando se usa, nunca ingreso',
  'credit.amountLabel': 'Monto del crédito',
  'credit.amountPlaceholder': 'Monto',
  'credit.reasonLabel': 'Motivo del crédito',
  'credit.reasonPlaceholder': 'Por qué',
  'credit.moneyIn': 'Dinero recibido',
  'credit.goodwill': 'Cortesía',
  'credit.kindCash': 'efectivo',
  'credit.kindGift': 'cortesía',
  'credit.reasonTail': ({ reason }) => ` · ${reason}`,
  'credit.spentAtCheckout': ' · usado en el cobro',
  'credit.empty': 'Nada en cuenta y nada usado.',

  /* ---------------------------------------------------------------- */
  /* Estante (§7.3.1)                                                  */
  /* ---------------------------------------------------------------- */

  'products.heading': 'Estante',
  'products.brandTail': ({ brand }) => ` · ${brand}`,
  'products.empty': 'Todavía no hay nada en el estante.',
  'products.nameLabel': 'Nombre del producto',
  'products.priceLabel': 'Precio del producto',
  'products.brand': 'Marca',
  'products.add': 'Agregar producto',

  /* ---------------------------------------------------------------- */
  /* Fotos de un producto o un tratamiento (B7)                         */
  /* ---------------------------------------------------------------- */

  'photos.heading': 'Fotos',
  'photos.none': 'Todavía no hay fotos.',
  'photos.needsConnection':
    'Las fotos van directamente a su sitio web, así que agregarlas o cambiarlas requiere ' +
    'conexión. No se guarda nada para enviarlo después.',
  'photos.offline': 'Este iPad no tiene conexión. Conéctese para agregar o cambiar fotos.',
  'photos.choose': 'Agregar fotos',
  'photos.altLabel': '¿Qué se ve en esta foto?',
  'photos.altPlaceholder': ({ name }) => `p. ej., ${name} sobre el mostrador`,
  'photos.altRequired':
    'Describa cada foto antes de subirla. Es lo que lee en voz alta un lector de pantalla en su sitio web.',
  'photos.upload': ({ count }) => `Subir ${String(count)} ${plural(count, 'foto', 'fotos')}`,
  'photos.discard': 'No agregar',
  'photos.waiting': 'en espera',
  'photos.percent': ({ percent }) => `${String(percent)} %`,
  'photos.failed': 'falló — inténtelo de nuevo',
  'photos.moveEarlier': ({ alt }) => `Mover «${alt}» antes`,
  'photos.moveLater': ({ alt }) => `Mover «${alt}» después`,
  'photos.remove': ({ alt }) => `Quitar «${alt}»`,
  'photos.removeConfirm': '¿Quitar esta foto de su sitio web?',
  'photos.removeYes': 'Quitarla',
  'photos.keep': 'Dejarla',
  'photos.full': ({ max }) =>
    `${String(max)} fotos es lo máximo que puede tener un artículo. Quite una para agregar otra.`,
  /* Por foto: en el sitio web o no (B10). */
  'photos.onWebsite': 'En el sitio web',
  'photos.offWebsite': 'No está en el sitio web',
  'photos.onWebsiteFor': ({ alt }) => `“${alt}” en el sitio web`,

  /* ---------------------------------------------------------------- */
  /* Qué va en el sitio web, y quién puede cambiarlo (B10)             */
  /* ---------------------------------------------------------------- */

  'catalog.showOnWebsite': 'Mostrar en el sitio web',
  'catalog.offWebsite': 'no está en el sitio web',
  'catalog.offWebsiteTitle': 'Se sigue vendiendo aquí; los clientes no lo ven en el sitio web.',
  'catalog.readOnlyStaff':
    'Solo el dueño cambia lo que vende el negocio. El dueño puede permitírselo al personal en Ajustes.',
  'catalog.readOnlySeat': 'Esta cuenta puede ver lo que vende el negocio, pero no cambiarlo.',
  'catalog.details': 'Detalles',
  'catalogEditors.heading': 'Quién puede cambiar lo que vendemos',
  'catalogEditors.hint':
    'Agregar, editar y retirar productos y servicios, sus fotos y lo que aparece en el sitio web.',
  'catalogEditors.owner': 'Solo el dueño',
  'catalogEditors.ownerAndStaff': 'El dueño y el personal',
  'catalogEditors.readonlyNote': 'Las cuentas de solo lectura nunca pueden.',
  'catalogEditors.saved': 'Guardado.',

  /* ---------------------------------------------------------------- */
  /* Ajustes                                                           */
  /* ---------------------------------------------------------------- */

  'settings.menu': 'Menú',
  'settings.serviceLine': ({ minutes, price }) => `${String(minutes)} min · ${price}`,
  'settings.optionLength': ({ minutes, price }) => `${String(minutes)} min ${price}`,
  'settings.optionNamed': ({ name, price }) => `${name} ${price}`,
  'settings.retiredCount': ({ count }) =>
    count === 1 ? '1 entrada retirada' : `${String(count)} entradas retiradas`,
  'settings.buffer': ({ minutes }) => ` · +${String(minutes)} min de margen`,
  'settings.bufferFromHouse': ' (valor de la casa)',
  'settings.bufferBefore': 'Margen antes (min)',
  'settings.bufferAfter': 'Margen después (min)',
  'settings.houseDefault': ({ minutes }) => `valor de la casa (${String(minutes)})`,
  'settings.counterOnly': 'solo mostrador',
  'settings.counterOnlyTitle': 'Fuera del menú del agente de reservas.',
  'settings.minutes': 'Minutos',
  'settings.addService': 'Agregar servicio',
  'settings.agentMayBook': 'el agente puede reservarlo',
  'settings.openingHours': 'Horario de atención',
  'settings.closed': 'cerrado',
  'settings.addWindow': '+ franja',
  'settings.saveWeek': 'Guardar la semana',
  'settings.closures': 'Cierres',
  'settings.blackoutSpan': ({ from, to }) => `${from} – ${to}`,
  'settings.blackoutReason': ({ reason }) => ` · ${reason}`,
  'settings.nothingClosed': 'No hay nada cerrado.',
  'settings.reason': 'Motivo',
  'settings.closeThisTime': 'Cerrar este horario',
  'settings.thisDevice': 'Este dispositivo',
  'settings.business': 'Negocio',
  'settings.timezone': 'Zona horaria',
  'settings.status': 'Estado',
  'settings.signedInAs': 'Sesión iniciada como',
  'settings.switchedOn': 'Activado',
  'settings.mayChange': 'Puede cambiar la agenda',
  'settings.canWriteYes': 'sí',
  'settings.canWriteNo': 'no — esta cuenta solo puede leer',

  /* ---------------------------------------------------------------- */
  /* El equipo (S2)                                                    */
  /* ---------------------------------------------------------------- */

  /*
   * `equipo` y no `personal`: la dueña habla de *su equipo*, y `personal` suena
   * a nómina. `sala` para el cuarto de tratamiento (no `habitación`, que en este
   * catálogo pertenece a hospedaje) y `cuenta` para el inicio de sesión, que es
   * la palabra que ya usa `settings.signedInAs`.
   */
  'team.title': 'Equipo',
  'team.justYouTitle': 'Solo está usted',
  'team.justYouBody':
    'Aquí todavía no hay nada que administrar. Su horario está en Ajustes, ' +
    'y el día que contrate a alguien, agréguela aquí.',
  'team.soloLane': ({ name }) => `Su calendario usa una sola línea, “${name}”.`,
  'team.people': 'Personas',
  'team.nobodyYet': 'Todavía nadie.',
  'team.addPerson': 'Agregar a alguien',
  'team.rooms': 'Salas y equipo',
  'team.noRooms': 'Todavía no hay salas.',
  'team.addRoom': 'Agregar una sala',
  'team.units': ({ units }) =>
    `${String(units)} ${plural(units, 'unidad intercambiable', 'unidades intercambiables')}`,
  'team.order': 'Orden',
  'team.login': 'Cuenta',
  'team.noLogin': 'sin cuenta',
  'team.signsInAs': ({ email }) => ` · inicia sesión como ${email}`,
  'team.someLogin': 'una cuenta de la lista',
  'team.followsHouse': 'Sigue el horario del negocio',
  'team.ownWeek': 'Tiene su propio horario',
  'team.giveOwnWeek': 'Darle su propio horario',
  'team.backToHouse': 'Volver al horario del negocio',
  'team.notWorking': 'no trabaja',
  'team.worksNoDays': 'Sin días de trabajo',
  'team.timeOff': 'Tiempo libre',
  'team.noTimeOff': 'Nada programado.',
  'team.bookTimeOff': 'Programar tiempo libre',
  'team.conflictsHeading': ({ count }) =>
    `${String(count)} ${plural(count, 'cita queda', 'citas quedan')} dentro de este periodo — ` +
    'muévalas o cancélelas usted misma.',
  'team.conflictLine': ({ when, who, what }) => `${when} · ${who} · ${what}`,
  'team.whoDoesWhat': 'Quién hace qué',
  'team.whoDoesWhatHint':
    'Una marca significa que esa persona o sala puede dar el servicio. ' +
    'Un servicio sin ninguna marca no se puede reservar.',
  'team.service': 'Servicio',
  'team.matrixCell': ({ service, who }) => `${who} da ${service}`,
  'team.needsBoth': 'Necesita una persona y una sala',
  'team.needsBothCell': ({ service }) => `${service} necesita una persona y una sala`,
  'team.needsBothHint':
    'Marcado, el servicio ocupa a la vez a una de sus personas y una de sus salas: ' +
    'se ofrece a una persona solo mientras haya una sala libre, y una sala solo mientras haya una persona.',

  /* ---------------------------------------------------------------- */
  /* Certificados de regalo (§7.3)                                     */
  /* ---------------------------------------------------------------- */

  'certificates.faceValue': 'A valor nominal',
  'certificates.expiredUnspent': 'Vencidos sin usar',
  'certificates.remainingOf': ({ remaining, value }) => `${remaining} de ${value}`,
  'certificates.empty': ({ where }) =>
    `Todavía no hay certificados. Emita uno ${where}; se redimen en el cobro, por código — nunca desde esta pantalla.`,
  'certificates.emptyHere': 'aquí',
  'certificates.emptyWithButton': ({ button }) => `con ${button}`,
  'certificates.needAmount': 'Un certificado necesita un monto o un servicio.',
  // Solicitudes de regalo: promesas sin pagar que el mostrador liquida.
  'giftRequests.title': 'Solicitudes de regalo',
  'giftRequests.unpaid':
    'Pedidas en línea y sin pagar. Venda la tarjeta aquí y pasa a ser un certificado.',
  'giftRequests.none': 'Nadie espera una tarjeta regalo.',
  'giftRequests.offline':
    'Esta lista necesita la red. Es una cola de personas a quienes llamar, así que no hay copia sin conexión.',
  'giftRequests.forService': ({ name, price }) => `${name} — ${price}`,
  'giftRequests.forWhom': ({ name }) => ` · para ${name}`,
  'giftRequests.sell': 'Vender este certificado',
  'giftRequests.whichCard': 'Cuál tarjeta',
  'giftRequests.markSold': 'Marcar como vendida',
  'giftRequests.cancel': 'Cancelar',
  'certificates.issuingAmount': ({ amount }) => `Emitiendo un certificado de ${amount}`,
  'certificates.issuingService': ({ name }) => `Emitiendo un certificado para ${name}`,
  'certificates.aService': 'un servicio',
  'certificates.amountLabel': 'Monto del certificado',
  'certificates.amountPlaceholder': 'Monto',
  'certificates.serviceLabel': 'Servicio del certificado',
  'certificates.lengthLabel': 'Duración',
  'certificates.namedLength': ({ name, length }) => `${name} · ${length}`,
  'certificates.catalogPrice': ({ amount, length }) =>
    `${amount} es el precio de catálogo para ${length}. Déjelo y la tarjeta vale exactamente eso; cámbielo para venderla por otro monto.`,
  'certificates.recipientLabel': 'Destinatario',
  'certificates.purchaserLabel': 'Comprador',
  'certificates.expiresLabel': 'Vence el',
  'certificates.issue': 'Emitir',
  // La pestaña de tarjetas de regalo: resumen, búsqueda, filtros y cómo se pagó.
  'giftcards.heading': 'Tarjetas de regalo',
  'giftcards.new': 'Tarjeta de regalo',
  'giftcards.summary': ({ count, amount }) =>
    `${String(count)} ${plural(count, 'pendiente', 'pendientes')} · ${amount} por canjear`,
  'giftcards.search': 'Buscar código, nombre, servicio…',
  'giftcards.searchLabel': 'Buscar tarjetas de regalo',
  'giftcards.filterLabel': 'Mostrar',
  'giftcards.filterOutstanding': 'Pendientes',
  'giftcards.filterRedeemed': 'Canjeadas',
  'giftcards.filterExpired': 'Vencidas',
  'giftcards.filterAll': 'Todas',
  'giftcards.giftAmount': 'Monto de regalo',
  'giftcards.whatAndValue': ({ what, value }) => `${what} · ${value}`,
  'giftcards.forWhom': ({ name }) => `para ${name}`,
  'giftcards.boughtBy': ({ name }) => `comprada por ${name}`,
  'giftcards.issued': ({ date }) => `emitida ${date}`,
  'giftcards.expires': ({ date }) => `vence ${date}`,
  'giftcards.expired': ({ date }) => `venció ${date}`,
  'giftcards.neverExpires': 'no vence',
  'giftcards.paidWith': ({ tender }) => `pagada con ${tender}`,
  'giftcards.noMatch': 'Ninguna tarjeta coincide con esa búsqueda.',
  'giftcards.noneInFilter': 'No hay nada aquí.',
  'giftcards.sheetLabel': 'Nueva tarjeta de regalo',
  'giftcards.amountOption': 'Monto de regalo (sin servicio)',
  'giftcards.howPaid': '¿Cómo se pagó?',
  'giftcards.needTender': 'Indique cómo se pagó antes de emitirla.',
  // Quién la tiene y quién la compró (0074): clientes del libro, elegidos o agregados aquí.
  'giftcards.walkIn': 'Sin cita / no es cliente',
  'giftcards.needRecipient':
    'Elija para quién es la tarjeta — una de sus clientes, o agréguela como nueva.',
  // El código: creado en el iPad, o el número impreso en una tarjeta física.
  'giftcards.codeLabel': 'Código de la tarjeta',
  'giftcards.codeMinted': 'El código se crea al emitirla.',
  'giftcards.codeUsePrinted': 'Usar el número impreso en la tarjeta',
  'giftcards.codeUseMinted': 'Crear un código',
  'giftcards.codePrintedLabel': 'Número impreso en la tarjeta',
  'giftcards.codePrintedPlaceholder': 'p. ej. GC-3632',
  'giftcards.codePrintedWillBe': ({ code }) => `Se guarda como ${code}`,
  'giftcards.codePrintedInvalid':
    'Escriba el número tal como está impreso — letras, dígitos y guiones, al menos 4 caracteres.',
  'giftcards.codeTaken': ({ name }) =>
    `Ese número ya está en otra tarjeta de regalo${name}. Revise la tarjeta, o deje que la app cree un código.`,
  'giftcards.codeTakenFor': ({ name }) => ` (para ${name})`,
  'giftcards.issuedHeading': 'Tarjeta de regalo emitida',
  'giftcards.notIssuedHeading': 'No se emitió',
  'giftcards.codeAria': ({ code }) => `Código de la tarjeta ${code}`,
  'giftcards.copy': 'Copiar código',
  'giftcards.copied': 'Copiado.',
  'giftcards.copySelected': 'El código está seleccionado — cópielo desde el menú.',
  'giftcards.statusPending':
    'Guardada en este iPad. Llega a sus libros en cuanto haya señal — el código no cambia.',
  'giftcards.statusSaved': 'Guardada en sus libros.',
  'giftcards.refusedCode':
    'Ese número ya está en otra tarjeta de regalo, así que esta no se emitió.',
  'giftcards.refusedClient': 'No se encontró a la cliente de esta tarjeta, así que no se emitió.',
  'giftcards.refused': ({ message }) => `No se emitió: ${message}`,
  'giftcards.fixIt': 'Volver y corregir',
  'giftcards.factCard': 'Tarjeta',
  'giftcards.factFor': 'Para',
  'giftcards.factFrom': 'De',
  'giftcards.factExpires': 'Vence',
  'giftcards.factPaid': 'Pagada',
  'giftcards.never': 'Nunca',
  'giftcards.done': 'Listo',
  // El selector de clientes: buscar por nombre o teléfono, o agregar a alguien nuevo.
  'clientPicker.search': 'Buscar nombre o teléfono',
  'clientPicker.noMatch': ({ search }) => `Ningún cliente coincide con «${search}».`,
  'clientPicker.new': '+ Cliente nuevo',
  'clientPicker.email': 'Correo',
  'clientPicker.change': 'Cambiar',
  'clientPicker.isNew': 'nueva — se agrega a la agenda con esto',
  'clientPicker.needName': 'Póngale un nombre a la cliente nueva.',
  'clientPicker.needContact': 'Agregue un teléfono o un correo, para poder encontrarla otra vez.',
  'clientPicker.badPhone': 'Ese teléfono no parece correcto.',
  'clientPicker.badEmail': 'Ese correo no parece correcto.',
  'clientPicker.taken': ({ name }) => `Ya es de ${name}.`,
  'clientPicker.useExisting': ({ name }) => `Usar a ${name}`,
  'certificates.footnote':
    'Emitirlo registra la venta con la fecha de hoy — un certificado es ingreso el día en que se vende, y la visita que pague después solo lleva el resto.',

  /* ---------------------------------------------------------------- */
  /* Reservar                                                          */
  /* ---------------------------------------------------------------- */

  'book.dialogLabel': 'Reservar una cita',
  'book.title': 'Reservar',
  'book.service': 'Servicio',
  'book.choose': 'Elija…',
  'book.serviceOption': ({ name, minutes }) => `${name} · ${String(minutes)} min`,
  'book.findService': 'Buscar en el menú',
  'book.noServiceMatch': ({ query }) => `Nada en el menú coincide con “${query}”`,
  'book.changeService': 'Cambiar',
  'book.priceRange': ({ from, to }) => `${from}–${to}`,
  'book.where': 'Dónde',
  'book.who': 'Quién',
  'book.room': 'Sala',
  'book.errorRoom': 'Elija una sala.',
  'book.starts': 'Empieza',
  'book.timeRange': ({ from, to }) => `${from} – ${to}`,
  'book.zoneNote': ({ timezone }) => `hora de ${timezone}`,
  'book.existingGuest': 'Cliente que ya está en la agenda',
  'book.someoneNew': 'Alguien nuevo…',
  'book.guest': 'Cliente',
  'book.errorService': 'Elija un servicio.',
  'book.errorWhere': 'Elija dónde se hace.',
  'book.errorTime': 'Elija un horario.',
  'book.errorName': 'Escriba el nombre del cliente.',
  'book.errorContact': 'Se necesita un teléfono o un correo para reservar a alguien nuevo.',
  'book.errorLength': 'El tiempo es un número entero de minutos entre 5 y 480.',
  'book.submit': 'Reservar',
  'book.submitRange': ({ range }) => `Reservar ${range}`,

  /* El control de tiempo (A1). */
  'book.length': 'Tiempo',
  'book.minutes': 'Minutos',
  'book.lengthMinus': ({ by }) => `${String(by)} minutos menos`,
  'book.lengthPlus': ({ by }) => `${String(by)} minutos más`,
  'book.lengthPlain': ({ minutes }) => `${String(minutes)} min`,
  'book.lengthChip': ({ minutes, price }) => `${String(minutes)} min · ${price}`,
  'book.adjustTime': 'Ajustar el tiempo',
  'book.adjustReset': 'Deshacer',
  'book.lengthBooked': ({ minutes, menu }) =>
    `${String(minutes)} min reservados · el menú dice ${String(menu)}`,
  'book.lengthFromClient': ({ minutes, catalog }) =>
    `con ella suele ser ${String(minutes)} min · menú ${String(catalog)} min`,
  'book.lengthFromClientOption': ({ minutes, catalog, option }) =>
    `con ella suele ser ${String(minutes)} min · ${option}, menú ${String(catalog)} min`,
  'book.rememberLength': ({ minutes, name, service }) =>
    `Recordar ${String(minutes)} min para ${service} de ${name}`,

  /* Las formas en que se vende, y lo que se le puede agregar (migración 0048). */
  'book.optionChip': ({ name, price }) => `${name} · ${price}`,
  'book.addOns': 'Agregar a esta cita',
  'book.addOnChoice': ({ name, price, minutes }) => `${name} · +${String(minutes)} min · ${price}`,
  'book.summaryLabel': 'Esta cita',
  'book.summaryLine': ({ name, minutes, price }) => `${name} · ${String(minutes)} min · ${price}`,
  'book.summaryAddOn': ({ name, minutes, price }) =>
    `+ ${name} · ${String(minutes)} min · ${price}`,
  'book.summaryTotal': ({ minutes, price }) => `Total · ${String(minutes)} min · ${price}`,
  'book.clash': ({ name, clock }) =>
    `Esto se encima con ${name} a las ${clock} en la copia de la agenda de este dispositivo. Reserve igual si usted sabe mejor.`,

  /* ---------------------------------------------------------------- */
  /* El toque sobre la cita (B1) y su campo de nota                    */
  /* ---------------------------------------------------------------- */

  'visit.dialogLabel': 'Visita',
  'visit.headline': ({ clock, service, minutes }) =>
    `${clock} · ${service} · ${String(minutes)} min`,
  'visit.serviceOption': ({ service, option }) => `${service} · ${option}`,
  'visit.addOns': 'Agregado',
  'visit.addOnLine': ({ price, minutes }) => `${price} · +${String(minutes)} min`,
  'visit.allergies': 'Alergias',
  'visit.preferences': 'Preferencias',
  'visit.lastVisit': 'Última visita',
  'visit.lastVisitLine': ({ day, service }) => `${day} — ${service}`,
  'visit.noRecentVisit': 'Nada en las semanas que guarda este dispositivo.',
  'visit.herLength': ({ service, minutes, catalog }) =>
    `Su ${service} suele tardar ${String(minutes)} min · menú ${String(catalog)} min`,
  'visit.notes': 'Notas',
  'visit.noNotes': 'Todavía no hay nada anotado.',
  'visit.purchases': 'Compras',
  'visit.birthdayToday': 'Cumpleaños hoy',
  'visit.birthdayTomorrow': 'Cumpleaños mañana',
  'visit.birthdaySoon': ({ days }) =>
    `Cumpleaños en ${String(days)} ${plural(days, 'día', 'días')}`,

  'note.add': 'Agregar una nota',
  'note.placeholder': 'Algo que valga la pena recordar',
  'note.dictate': 'Dictar',
  'note.listening': 'Escuchando…',

  /* ---------------------------------------------------------------- */
  /* Cobro (§6.5)                                                      */
  /* ---------------------------------------------------------------- */

  'checkout.dialogLabel': 'Cobro',
  'checkout.quickSale': 'Venta rápida',
  'checkout.closeOut': ({ name }) => `Cerrar la cuenta — ${name}`,
  'checkout.guestFallback': 'cliente',
  'checkout.guest': 'Cliente',
  'checkout.listAndOff': ({ off }) => ` de lista · ${off} menos cada uno`,
  'checkout.qtyLabel': ({ name }) => `Cantidad de ${name}`,
  'checkout.priceLabel': ({ name }) => `Precio de ${name}`,
  'checkout.fromReservation': ({ name }) =>
    `Del pedido para recoger de ${name} — al precio que se le cotizó.`,
  'checkout.reservationLineMissing':
    'Una línea nombraba un producto que ya no está en el catálogo y quedó fuera. Agréguelo a mano.',
  'checkout.discountLabel': 'Descuento',
  'checkout.certCodeLabel': 'Código del certificado',
  'checkout.certCodePlaceholder': 'Código',
  'checkout.lookUp': 'Buscar',
  'checkout.noSuchCode': 'No hay ningún certificado con ese código.',
  'checkout.certExpired': ({ date }) => `Ese certificado venció el ${date}.`,
  'checkout.certEmpty': 'A ese certificado no le queda nada.',
  'checkout.certLeft': ({ code, amount }) => `${code} · quedan ${amount}`,
  'checkout.certItem': ({ name }) => ` · ${name}`,
  'checkout.certAmountLabel': 'Monto del certificado',
  'checkout.certAmountPlaceholder': 'todo',
  'checkout.useDepositLabel': 'Usar el depósito',
  'checkout.depositTaken': ({ amount }) => `${amount} ya cobrado`,
  'checkout.depositUnknown': 'lo que quede del depósito registrado',
  'checkout.useCreditLabel': 'Usar el crédito en cuenta',
  'checkout.creditPools': ({ gift, cash }) =>
    `${gift} de cortesía (se usa primero, como descuento) · ${cash} en efectivo`,
  'checkout.giftAmountLabel': 'Monto del crédito de cortesía',
  'checkout.giftAmountPlaceholder': 'cortesía: todo',
  'checkout.cashAmountLabel': 'Monto del crédito en efectivo',
  'checkout.cashAmountPlaceholder': 'efectivo: todo',
  /* Los seis medios de pago que se REGISTRAN, nunca se procesan (B4). */
  'checkout.tender.cash': 'efectivo',
  'checkout.tender.check': 'cheque',
  'checkout.tender.venmo': 'Venmo',
  'checkout.tender.paypal': 'PayPal',
  'checkout.tender.card': 'tarjeta',
  'checkout.tender.other': 'otro',
  'checkout.tenderLabelLabel': '¿Cuál fue?',
  'checkout.tenderLabelPlaceholder': 'Zelle, transferencia…',
  /* Split tender, tips and refunds (migration 0046). */
  'checkout.addPayment': '+ Añadir otro pago',
  'checkout.removePayment': 'Quitar',
  'checkout.partAmountLabel': ({ n }: { n: number }) => `Importe del pago ${String(n)}`,
  'checkout.partTenderLabel': ({ n }: { n: number }) => `Cómo se hizo el pago ${String(n)}`,
  'checkout.partRest': 'el resto',
  'checkout.splitHint': 'El último cubre lo que quede.',
  'checkout.tip': 'Propina',
  'checkout.tipLabel': 'Importe de la propina',
  'checkout.tipHint': 'Para la persona, no para el negocio: nunca cuenta como ingreso.',
  'checkout.tipPercent': ({ percent }: { percent: number }) => `${String(percent)}%`,
  'checkout.tipClear': 'Sin propina',
  'checkout.tax': 'Impuestos',
  'checkout.note': 'Nota',
  /* La caja registradora (B4). */
  'checkout.searchLabel': 'Buscar un artículo',
  'checkout.searchPlaceholder': 'Escribe unas letras…',
  'checkout.searchNoMatch': ({ query }) => `Nada coincide con «${query}».`,
  'checkout.keypadLabel': 'Teclado numérico',
  'checkout.lineNoteLabel': ({ name }) => `Qué fue este ${name}`,
  'checkout.lineNotePlaceholder': 'pañuelo coral',
  'checkout.doneDialogLabel': 'Venta registrada',
  'checkout.doneTitle': 'Registrada',
  'checkout.doneTender': ({ tender }) => `· ${tender}`,
  'checkout.doneQueued': 'En cola. Se envía en cuanto este dispositivo tenga señal.',
  'checkout.needsALine': 'Un cobro necesita al menos una línea.',
  /* El carrito (2026-09-30): el catálogo a un lado, esta venta al otro. */
  'checkout.walkIn': 'Cliente sin cita',
  'checkout.guestButton': ({ name }) => `Cliente: ${name}`,
  'checkout.guestChange': 'Cambiar',
  'checkout.guestSearchLabel': 'Buscar clientes',
  'checkout.guestSearchPlaceholder': 'Nombre o teléfono',
  'checkout.guestNoMatch': ({ query }) => `Ningún cliente coincide con «${query}».`,
  'checkout.catalogLabel': 'Lo que vendes',
  'checkout.catalogEmpty': 'Todavía no hay nada en el menú.',
  'checkout.searchResults': 'Resultados',
  'checkout.tabServices': 'Servicios',
  'checkout.tabProducts': 'Productos',
  'checkout.moreServices': 'Más',
  'checkout.tileService': ({ minutes, price }) => `${price} · ${String(minutes)} min`,
  'checkout.tileLengths': ({ count, price }) => `desde ${price} · ${String(count)} duraciones`,
  'checkout.tileAddOn': ({ minutes, price }) => `+${price} · ${String(minutes)} min`,
  'checkout.minutes': ({ minutes }) => `${String(minutes)} min`,
  'checkout.priceAtTill': 'precio en caja',
  'checkout.chooseLength': ({ name }) => `${name} — ¿qué duración?`,
  'checkout.addOnsFor': ({ name }) => `Añadir a ${name}`,
  'checkout.addOnOf': ({ name }) => `con ${name}`,
  'checkout.cartSection': 'Esta venta',
  'checkout.cartLabel': 'Artículos de esta venta',
  'checkout.cartEmpty': 'Todavía no hay nada en esta venta — toca un artículo para añadirlo.',
  'checkout.lessQty': ({ name }) => `Uno menos de ${name}`,
  'checkout.moreQty': ({ name }) => `Uno más de ${name}`,
  'checkout.removeLine': ({ name }) => `Quitar ${name}`,
  'checkout.priceSheetTitle': ({ name }) => `¿Cuánto cuesta ${name}?`,
  'checkout.priceSheetAmount': 'Precio',
  'checkout.priceSheetAdd': 'Añadir a la venta',
  'checkout.priceSheetSave': 'Cambiar el precio',
  'checkout.keypadBackspace': 'Borrar',
  'checkout.lineNoteHint': '¿Qué fue? (opcional)',
  'checkout.subtotalLabel': 'Subtotal',
  'checkout.addDiscount': 'Añadir descuento',
  'checkout.removeDiscount': 'Quitar el descuento',
  'checkout.addTax': 'Añadir impuestos',
  'checkout.removeTax': 'Quitar los impuestos',
  'checkout.totalLabel': 'Total',
  'checkout.rowGiftCard': 'Tarjeta de regalo',
  'checkout.rowDeposit': 'Depósito pagado',
  'checkout.rowCredit': 'Crédito en cuenta',
  'checkout.rowToPay': 'Por pagar ahora',
  'checkout.howPaidQuestion': '¿Cómo se pagó?',
  'checkout.tenderButton.cash': 'Efectivo',
  'checkout.tenderButton.check': 'Cheque',
  'checkout.tenderButton.venmo': 'Venmo',
  'checkout.tenderButton.paypal': 'PayPal',
  'checkout.tenderButton.card': 'Tarjeta',
  'checkout.tenderButton.other': 'Otro',
  'checkout.useGiftCard': 'Usar una tarjeta de regalo',
  'checkout.forgetGiftCard': 'No usar tarjeta de regalo',
  'checkout.forgetCredit': 'No usar el crédito en cuenta',
  'checkout.splitPayment': 'Dividir el pago',
  'checkout.addNote': 'Añadir una nota a esta venta',
  'checkout.charge': ({ amount, tender }) => `Cobrar ${amount} · ${tender}`,
  'checkout.chargeAmount': ({ amount }) => `Cobrar ${amount}`,
  'checkout.chargePlain': 'Cobrar',
  'checkout.chargeSplit': 'pago dividido',
  'checkout.completeSale': 'Completar la venta — no queda nada por pagar',
  'checkout.whyEmpty': 'Primero añade algo a la venta.',
  'checkout.whyTender': 'Elige cómo se pagó.',

  /* ---------------------------------------------------------------- */
  /* Informes — los seis del libro mayor                               */
  /* ---------------------------------------------------------------- */

  'reports.notSwitchedOn': 'Los informes no están activados para este negocio.',
  'reports.noneYet':
    'Todavía no hay informes en este dispositivo y no se puede alcanzar el libro mayor. Aparecerán la próxima vez que este iPad esté en línea.',
  'reports.reading': 'Leyendo el libro mayor…',
  'reports.title': 'Informes',
  'reports.couldNotReach': ' · no se pudo alcanzar el libro mayor ahora',
  'reports.last30': ({ from, to }) => `Últimos 30 días · ${from} a ${to}`,
  'reports.moneyIn': 'Dinero recibido',
  'reports.givenAway': 'Regalado',
  'reports.checkouts': 'Cobros',
  'reports.visits': 'Visitas',
  'reports.noShows': 'No llegaron',
  'reports.upcoming': 'Agendado por delante',
  'reports.sourceLine': ({ settlement, deposit, certificate, topup }) =>
    `${settlement} en el mostrador · ${deposit} en depósitos · ${certificate} en certificados · ${topup} en recargas de crédito`,
  'reports.tenderLine': 'En el mostrador:',
  'reports.revenueByMonth': 'Ingresos por mes',
  'reports.colMonth': 'Mes',
  'reports.colCounter': 'Mostrador',
  'reports.colDeposits': 'Depósitos',
  'reports.colCertificates': 'Certificados',
  'reports.colCreditTopups': 'Recargas de crédito',
  // Spanish keeps the word. It is the only honest column header, and the suite
  // names this key as the one deliberate overlap with English.
  'reports.colTotal': 'Total',
  'reports.dateOfMoney':
    'Cada centavo en la fecha en que llegó el dinero — un depósito cobrado en marzo es de marzo, sea cual sea el mes de la visita.',
  'reports.whatSells': ({ from, to }) => `Lo que se vende · ${from} a ${to}`,
  'reports.colItem': 'Artículo',
  'reports.colSold': 'Vendidos',
  'reports.colAtList': 'A precio de lista',
  'reports.colGivenAway': 'Regalado',
  'reports.colNet': 'Neto',
  'reports.nothingSold': 'No se vendió nada en este período.',
  'reports.busiestDays': 'Días de más movimiento',
  'reports.bestGuests': 'Mejores clientes · el último año',
  'reports.lastVisit': ({ date }) => ` · última ${date}`,
  'reports.nobodyYet': 'Todavía nadie.',
  'reports.worthACall': 'Vale la pena llamar',
  'reports.daysSince': ({ days, usual }) =>
    `${String(days)} días desde la última, normalmente cada ${String(usual)}`,
  'reports.phoneTail': ({ phone }) => ` · ${phone}`,
  'reports.nobodyOverdue': 'Nadie está atrasado — quien tiene la costumbre la está cumpliendo.',

  /* ---------------------------------------------------------------- */
  /* Analítica — la réplica de lectura                                 */
  /* ---------------------------------------------------------------- */

  'analytics.heading': 'Informes más detallados',
  'analytics.labelRevenue': 'Ingresos',
  'analytics.labelBookings': 'Reservas',
  'analytics.labelUtilization': 'Ocupación',
  'analytics.labelServices': 'Servicios',
  'analytics.labelGuests': 'Clientes',
  'analytics.asking': 'Preguntando qué hay disponible…',
  'analytics.nonePublished': 'Ninguno publicado.',
  'analytics.navLabel': 'Informes',
  'analytics.last': 'Últimos',
  'analytics.days': 'días',
  'analytics.by': 'Por',
  'analytics.grainDay': 'día',
  'analytics.grainWeek': 'semana',
  'analytics.grainMonth': 'mes',
  'analytics.cap': ({ cap }) => `Este informe responde como máximo ${String(cap)} días a la vez`,
  'analytics.capAsking': ({ asked }) => ` — pidiendo ${String(asked)}`,
  'analytics.noTable': ({ id }) =>
    `Esta app todavía no tiene una tabla para “${id}”. El servicio lo publica en`,
  'analytics.reading': 'Leyendo…',
  'analytics.couldNotRead': ({ message }) => `No se pudo leer: ${message}`,
  'analytics.window': ({ from, to }) => `${from} a ${to}`,
  'analytics.clamped': ' · recortado al máximo de este informe',
  'analytics.noMoney': 'No hubo dinero en este período.',
  'analytics.credit': 'Crédito',
  'analytics.cashTopUps': 'Recargas en efectivo — ingreso',
  'analytics.cashSpent': 'Efectivo usado — ingreso de una fecha anterior',
  'analytics.giftIssued': 'Cortesía emitida — nunca ingreso',
  'analytics.giftSpent': 'Cortesía usada — un descuento',
  'analytics.recognized': ({ total, count }) =>
    `${total} reconocidos en ${String(count)} ${plural(count, 'cobro', 'cobros')}.`,
  'analytics.colSource': 'Origen',
  'analytics.colBookings': 'Reservas',
  'analytics.colCovers': 'Comensales',
  'analytics.colCancelled': 'Canceladas',
  'analytics.colNoShows': 'No llegaron',
  'analytics.colMedianLead': 'Anticipación media',
  'analytics.colSample': 'Muestra',
  'analytics.rowAll': 'Todo',
  'analytics.leadHours': ({ hours }) => `${String(hours)} h`,
  'analytics.colResource': 'Recurso',
  'analytics.colLanes': 'Carriles',
  'analytics.colOpenHours': 'h abiertas',
  'analytics.colSellableHours': 'h vendibles',
  'analytics.colOccupiedHours': 'h ocupadas',
  'analytics.colUsed': 'Uso',
  'analytics.nothingOpen': 'No hubo nada abierto en este período.',
  'analytics.colBooked': 'Reservado',
  'analytics.colAvgParty': 'Grupo promedio',
  'analytics.namesIn': ({ locale }) => `Nombres en ${locale}.`,
  'analytics.tileGuests': 'Clientes',
  'analytics.tileNew': 'Nuevos',
  'analytics.tileReturning': 'Recurrentes',
  'analytics.tileRepeatRate': 'Tasa de repetición',
  'analytics.colGuest': 'Cliente',
  'analytics.colVisits': 'Visitas',
  'analytics.colLifetime': 'Histórico',
  'analytics.colFirstSeen': 'Primera vez',
  'analytics.colLastSeen': 'Última vez',

  /* ---------------------------------------------------------------- */
  /* Pedidos — la barra de la cocina (§3.6)                            */
  /* ---------------------------------------------------------------- */

  'orders.notSwitchedOn': 'Los pedidos no están activados para este negocio.',
  'orders.title': 'Pedidos',
  'orders.couldNotReach': ' · no se pudo alcanzar la cocina ahora',
  'orders.reading': 'Leyendo la barra…',
  'orders.noneOn': ({ day }) => `No hay pedidos el ${day}.`,
  'orders.lanePlaced': 'Recibidos',
  'parcels.notSwitchedOn': 'La tienda no está activada para este negocio.',
  'parcels.title': 'Paquetes',
  'parcels.couldNotReach': ' · no se pudo consultar la tienda ahora',
  'parcels.reading': 'Leyendo los paquetes…',
  'parcels.none': 'Todavía no hay paquetes.',
  'parcels.lanePaid': 'Por enviar',
  'parcels.laneSubmitted': 'Con el fabricante',
  'parcels.laneShipped': 'En camino',
  'parcels.stuckHeading': ({ count }) =>
    `${String(count)} ${plural(count, 'paquete no salió', 'paquetes no salieron')}`,
  'parcels.stuckTitle': ({ name }) => `El paquete de ${name} no salió`,
  'parcels.stuckBody': ({ attempts, total, clock }) =>
    `Pagado ${total}, pedido ${clock}. El fabricante lo rechazó ${String(attempts)} ${plural(attempts, 'vez', 'veces')} y los reintentos se agotaron. No se ha enviado nada.`,
  'parcels.lastError': ({ error }) => `Última respuesta: ${error}`,
  'parcels.retry': 'Enviarlo de nuevo',
  'parcels.markDelivered': 'Entregado',
  'parcels.deliveredHeading': 'Entregados',
  'parcels.itemsAndTotal': ({ count, total }) =>
    `${String(count)} ${plural(count, 'artículo', 'artículos')} · ${total}`,
  'parcels.placedAt': ({ clock }) => `Pedido ${clock}`,
  'parcels.providerRef': ({ provider, ref, draft }) =>
    `${provider} #${ref}${draft ? ' (borrador)' : ''}`,
  'orders.laneConfirmed': 'Confirmados',
  'orders.lanePreparing': 'En preparación',
  'orders.laneReady': 'Listos',
  'orders.laneCompleted': 'Entregados',
  'orders.verbPlaced': 'Reabrir',
  'orders.verbConfirmed': 'Confirmar',
  'orders.verbPreparing': 'Empezar',
  'orders.verbReady': 'Listo',
  'orders.verbCompleted': 'Entregado',
  'orders.verbCancelled': 'Cancelar',
  'orders.counter': 'Mostrador',
  'orders.itemsAndTotal': ({ count, total }) =>
    `${String(count)} ${plural(count, 'artículo', 'artículos')} · ${total}`,
  'orders.pickup': ({ clock }) => `recoge ${clock}`,
  'orders.toCollect': ({ amount }) => `${amount} por cobrar`,
  'orders.paid': 'pagado',
  'orders.cancelledHeading': 'Cancelados',
  'orders.cancelledSummary': ({ total, clock }) => `${total} · ${clock}`,
  'orders.dialogLabel': 'Pedido',
  'orders.counterOrder': 'Pedido de mostrador',
  'orders.placedAt': ({ clock }) => `Recibido ${clock}`,
  'orders.pickupTail': ({ clock }) => ` · recoge ${clock}`,
  'orders.qty': ({ qty }) => `${String(qty)}×`,
  'orders.sized': ({ name, size }) => `${name} · ${size}`,
  'orders.modifierWithPrice': ({ name, amount }) => `${name} ${amount}`,
  'orders.alreadyPaid': ({ amount }) => `${amount} ya pagado · `,
  'orders.paidInFull': 'pagado por completo',
  /* Los controles de la cocina (docs/verticals-plan.md V1). */
  'orders.prepMinutes': 'Minutos de preparación',
  'orders.prepPlaceholder': 'min.',
  'orders.prepTail': ({ minutes }: { minutes: number }) => ` · ${String(minutes)} min`,
  'orders.boardTitle': 'Tablero de agotados',
  'orders.boardShow': 'Mostrar el menú',
  'orders.boardHide': 'Ocultar el menú',
  'orders.boardReading': 'Leyendo el menú…',
  'orders.boardEmpty': 'Todavía no hay platos en el menú.',
  'orders.boardNote':
    'Agotado esta noche, no retirado del menú: un plato marcado como agotado sigue en el menú del cliente como agotado, y un pedido que lo pida se rechaza.',
  'orders.boardRetired': 'retirado',
  'orders.soldOut': 'agotado',
  'orders.eightySix': 'Marcar agotado',
  'orders.backOn': 'Reponer',
  'orders.flipChoice': ({ name, verb }: { name: string; verb: string }) => `${verb}: ${name}`,

  /* ---------------------------------------------------------------- */
  /* Salidas — el muelle (§4.8)                                        */
  /* ---------------------------------------------------------------- */

  'departures.notSwitchedOn': 'Las salidas no están activadas para este negocio.',
  'departures.title': 'Salidas',
  'departures.couldNotReach': ' · no se pudo alcanzar la oficina ahora',
  'departures.reading': 'Leyendo el itinerario…',
  'departures.noneOn': ({ day }) => `No hay nada programado para zarpar el ${day}.`,
  'departures.noVessel': 'sin bote asignado',
  'departures.privateCharter': ' · chárter privado',
  'departures.weather': 'Clima',
  'departures.cancelled': 'Cancelada',
  'departures.reasonTail': ({ reason }) => ` — ${reason}`,
  'departures.seats': ({ claimed, total }) => `${String(claimed)}/${String(total)} lugares`,
  'departures.parties': ({ count }) => `${String(count)} ${plural(count, 'grupo', 'grupos')}`,
  'departures.manifest': 'Manifiesto',
  'departures.readingManifest': 'Leyendo el manifiesto…',
  'departures.notFound': 'No se encontró.',
  'departures.sheetTitle': ({ name, clock }) => `${name} · ${clock}`,
  'departures.summaryLine': ({ vessel, claimed, total, parties }) =>
    `${vessel} · ${String(claimed)} de ${String(total)} lugares · ${parties}`,
  'departures.cancelledTail': ({ word, reason }) =>
    reason === null ? ` · ${word}` : ` · ${word} — ${reason}`,
  'departures.weatherLower': 'clima',
  'departures.cancelledLower': 'cancelada',
  'departures.seatCount': ({ count }) => `${String(count)} ${plural(count, 'lugar', 'lugares')}`,
  'departures.lanes': ({ lanes }) => ` · carriles ${lanes}`,
  'departures.ticketLine': ({ seats, name }) => `${String(seats)}× ${name}`,
  'departures.ticketsTail': ({ tickets }) => ` · ${tickets}`,
  'departures.nobodyBooked': 'Nadie reservó.',
  'departures.entryCancelled': ' · cancelada',
  // «Reconocido», nunca «firmado».
  'departures.waiverOk': ({ version }) => ` · reconocido (v${String(version)})`,
  'departures.waiverNone': ' · no se pidió exención',
  'departures.noticeTexted': ' · avisado por mensaje',
  'departures.noticeNoConsent': ' · sin avisar — sin consentimiento',
  'departures.noticeNoPhone': ' · sin avisar — sin teléfono',
  'departures.called': ({ bookings, seats }) =>
    `Aviso dado. Se ${plural(bookings, 'canceló', 'cancelaron')} ${String(bookings)} ${plural(bookings, 'reserva', 'reservas')} y se ${plural(seats, 'liberó', 'liberaron')} ${String(seats)} ${plural(seats, 'lugar', 'lugares')}. No se cobró ninguna tarifa; el dinero ya cobrado se devuelve desde el panel de PayPal.`,
  'departures.calledTexted': ({ texted, notTexted }) =>
    ` ${String(texted)} ${plural(texted, 'huésped avisado', 'huéspedes avisados')} por mensaje; ${String(notTexted)} sin avisar — mira cada fila.`,
  'departures.confirmLead': 'Esto cancela ',
  'departures.confirmMid': ({ count }) =>
    plural(count, ' reserva que cubre ', ' reservas que cubren '),
  'departures.confirmTail': ({ count }) =>
    `${plural(count, ' lugar', ' lugares')}, y se le avisa al socio por cada una; a cada huésped que aceptó mensajes se le avisa por texto. No se cobra ninguna tarifa de cancelación; devolver lo ya cobrado es una acción del dueño en PayPal.`,
  'departures.why': 'Por qué',
  'departures.reasonPlaceholder': 'Marejada de seis pies en el north drop',
  'departures.reasonKind': 'Tipo de motivo',
  'departures.kindWeather': 'Clima — el mar dijo que no',
  'departures.kindOperator': 'Operador — decidimos no salir',
  'departures.keepIt': 'Dejarla',
  'departures.calling': 'Avisando…',
  'departures.cancelN': ({ count }) =>
    `Cancelar ${String(count)} ${plural(count, 'reserva', 'reservas')}`,
  'departures.weatherCall': 'Suspender por clima',
  'departures.sellSeat': 'Vender un asiento',
  'departures.sellTitle': 'Vender un asiento',
  'departures.tierSeats': ({ name, price }) => `${name} · ${price} c/u`,
  'departures.seatsLeft': ({ count }) =>
    `${String(count)} ${count === 1 ? 'asiento libre' : 'asientos libres'}`,
  'departures.total': 'Tarifa total',
  'departures.totalNote':
    'La tarifa es la del catálogo; el servidor vuelve a calcularla al vender los asientos.',
  'departures.waiverTitle': ({ title, version }) => `${title} (v${String(version)})`,
  'departures.waiverTick': 'Leído al cliente y aceptado en el mostrador',
  'departures.errorSeats': 'Elija al menos un asiento.',
  'departures.errorWaiver': 'El cliente debe aceptar la exención antes de vender un asiento.',
  'departures.selling': 'Vendiendo…',
  'departures.sell': ({ count }) =>
    `Vender ${String(count)} ${count === 1 ? 'asiento' : 'asientos'}`,
  'departures.sold': ({ name, count }) =>
    `${String(count)} ${count === 1 ? 'asiento vendido' : 'asientos vendidos'} a ${name}.`,
  'departures.queued': 'En cola — el asiento se vende en cuanto se pueda contactar la oficina.',

  /* ---------------------------------------------------------------- */
  /* Recepción — hospedaje (§4.7)                                      */
  /* ---------------------------------------------------------------- */

  'frontdesk.notSwitchedOn': 'El hospedaje no está activado para este negocio.',
  'frontdesk.couldNotReach': ' · no se pudo alcanzar la recepción ahora',
  'frontdesk.reading': 'Leyendo el día…',
  'frontdesk.arriving': 'Llegan',
  'frontdesk.arrivingBlank': 'Nadie hace entrada.',
  'frontdesk.leaving': 'Salen',
  'frontdesk.leavingBlank': 'Nadie hace salida.',
  'frontdesk.inHouse': 'En la casa',
  'frontdesk.inHouseBlank': 'La casa está vacía.',
  'frontdesk.room': ({ room }) => `habitación ${String(room)}`,
  'frontdesk.nightsAndGuests': ({ nights, guests }) =>
    `${String(nights)} ${plural(nights, 'noche', 'noches')} · ${String(guests)} ${plural(guests, 'huésped', 'huéspedes')}`,
  'frontdesk.nothingTaken': 'todavía no se ha cobrado nada',
  'frontdesk.paidInFull': 'pagado por completo',
  'frontdesk.depositHeld': ({ amount }) => `${amount} de depósito retenido`,
  'frontdesk.dialogLabel': 'Estadía',
  'frontdesk.staySummary': ({ roomType, room, nights, guests, status }) =>
    `${roomType} · habitación ${String(room)} · ${String(nights)} ${plural(nights, 'noche', 'noches')} · ${String(guests)} ${plural(guests, 'huésped', 'huéspedes')} · ${status}`,
  'frontdesk.folio': 'Cuenta',
  'frontdesk.takenAlready': 'Ya cobrado',
  'frontdesk.stillToCollect': 'Por cobrar',
  'frontdesk.feeRecorded': 'Tarifa de cancelación registrada',
  'frontdesk.waived': ({ amount }) => `exonerada — la política habría cobrado ${amount}`,
  'frontdesk.policyFee': ({ tier }) =>
    tier === null ? 'la tarifa de la política' : `la tarifa de la política · nivel ${tier}`,
  'frontdesk.daysOutLine': ({ days, amount }) =>
    `· ${String(days)} ${plural(days, 'día', 'días')} antes · sobre ${amount} de alojamiento`,
  'frontdesk.policyRef': ({ ref }) => ` · ${ref}`,
  'frontdesk.recordedNotCharged':
    'Registrada, no cobrada. Devolver lo ya cobrado es una acción del dueño en PayPal (§4.7).',
  'frontdesk.confirmLead': 'La tarifa es la ',
  'frontdesk.confirmPolicy': 'de la política',
  'frontdesk.confirmMid': ({ nights, amount }) =>
    `, calculada por el servidor sobre ${plural(nights, 'la', 'las')} ${String(nights)} ${plural(nights, 'noche', 'noches')} tal como se vendieron (${amount}) y qué tan cerca está la fecha. Queda `,
  'frontdesk.confirmStrong': 'registrada, nunca cobrada',
  'frontdesk.confirmTail':
    ' — no se mueve dinero en ningún sentido, y el monto exacto se muestra aquí en cuanto se calcula. La decisión de la recepción es la que la política no puede tomar.',
  'frontdesk.keepBooking': 'Dejar la reserva',
  'frontdesk.cancelWaive': 'Cancelar y exonerar la tarifa',
  'frontdesk.cancelCharge': 'Cancelar y cobrar la tarifa de la política',
  'frontdesk.cancelling': 'Cancelando…',
  'frontdesk.cancelStay': 'Cancelar esta estadía',
  'frontdesk.bookStay': 'Reservar una estadía',
  'frontdesk.bookDialogLabel': 'Reservar una estadía',
  'frontdesk.checkIn': 'Entrada',
  'frontdesk.checkOut': 'Salida',
  'frontdesk.guestsField': 'Huéspedes',
  'frontdesk.roomField': 'Habitación',
  'frontdesk.pickARoom': 'Elija una habitación…',
  'frontdesk.roomOption': ({ name, price, nights }) =>
    `${name} · ${price} por ${String(nights)} ${nights === 1 ? 'noche' : 'noches'}`,
  'frontdesk.roomFull': ({ name }) => `${name} · ninguna libre en esas fechas`,
  'frontdesk.quoting': 'Cotizando esas fechas…',
  'frontdesk.quoteFailed': 'No se pudieron cotizar esas fechas.',
  'frontdesk.quoteBreakdown': ({ nightly, fees, tax }) =>
    `${nightly} noches · ${fees} cargos · ${tax} impuesto`,
  'frontdesk.quoteNote':
    'El total es del servidor; vuelve a calcular el calendario al escribir el folio.',
  'frontdesk.errorDates': 'La salida debe ser posterior a la entrada.',
  'frontdesk.errorRoom': 'Elija una habitación.',
  'frontdesk.booking': 'Reservando…',
  'frontdesk.book': 'Reservar',
  'frontdesk.booked': ({ name, room }) => `${name} queda en ${room}.`,
  'frontdesk.queued': 'En cola — la estadía se reserva en cuanto se pueda contactar el mostrador.',
  /* V3 — los calendarios que dos sistemas comparten. "Canal" antes que
     "calendario" en la nota: para el mostrador la cosa que vendió la
     habitación es Airbnb, no un archivo. */
  'frontdesk.calendarsHeading': ({ count }) =>
    `${String(count)} ${plural(count, 'calendario del que no sabemos nada', 'calendarios de los que no sabemos nada')}`,
  'frontdesk.calendarRoom': ({ room_type, room }) => `${room_type} · habitación ${String(room)}`,
  'frontdesk.calendarNeverPulled': 'nunca consultado',
  'frontdesk.calendarLastError': ({ error }) => `último intento: ${error}`,
  'frontdesk.calendarStaleNote': ({ hours }) =>
    `Un calendario aparece aquí cuando llevamos ${String(hours)} ${plural(hours, 'hora', 'horas')} sin poder leerlo, o cuando falló el último intento. Las reservas hechas en ese canal desde entonces no están en el calendario de esta casa — consulte el canal antes de entregar la habitación.`,
  'frontdesk.collisionsHeading': ({ count }) =>
    `${String(count)} ${plural(count, 'habitación vendida dos veces', 'habitaciones vendidas dos veces')}`,
  'frontdesk.collisionTitle': ({ room_type, room }) =>
    `${room_type}, habitación ${String(room)} — reservada por los dos lados`,
  'frontdesk.collisionOurs': ({ name, from, to }) => `La nuestra: ${name}, de ${from} a ${to}.`,
  'frontdesk.collisionTheirs': ({ calendar, summary, from, to }) =>
    `La de ellos: ${calendar} — ${summary}, de ${from} a ${to}.`,
  'frontdesk.collisionNothingCancelled':
    'No se ha cancelado nada y nada se cancelará solo. Decida qué reserva se mueve y cancélela desde su propia pantalla; esta tarjeta desaparece cuando lo haga uno de los dos lados.',

  /* ---------------------------------------------------------------- */
  /* Enlaces de pago (§3.7)                                            */
  /* ---------------------------------------------------------------- */

  'paylink.dialogLabel': 'Enlace de pago',
  'paylink.title': 'Enlace de pago',
  'paylink.approvedNotCaptured': ' · aprobado, sin cobrar',
  'paylink.expires': ({ clock }) => ` · vence ${clock}`,
  'paylink.paidAt': ({ clock }) => ` · pagado ${clock}`,
  'paylink.copy': 'Copiar',
  'paylink.copied': 'Copiado',
  'paylink.none': 'No se ha enviado ningún enlace para esta reserva.',
  'paylink.amountLabel': 'Monto — anulación del dueño (§6.5)',
  'paylink.amountPlaceholder': 'déjelo en blanco para la política de depósito',
  'paylink.amountHint':
    'En blanco significa que la política de depósito del negocio pone el precio, que es lo único que puede obtener un socio o el sitio del huésped. Un número aquí es el dueño cobrando lo que decide en su propia caja, y el enlace deja registrado que lo fijó el personal.',
  'paylink.notAnAmount': 'Eso no es un monto. Déjelo en blanco para usar la política de depósito.',
  'paylink.creating': 'Creando…',
  'paylink.create': 'Crear enlace',
  /* ---------------------------------------------------------------- */
  /* Albums (docs/tour-photos-design.md decision 5)                    */
  /* ---------------------------------------------------------------- */

  'albums.notSwitchedOn': 'Las fotos no están activadas para este negocio.',
  'albums.title': 'Álbumes',
  'albums.couldNotReach': ' · no se pudo conectar con la oficina',
  'albums.reading': 'Leyendo los álbumes…',
  'albums.none': 'Todavía no hay álbumes. Cree uno para la salida de hoy.',
  'albums.untitled': 'Álbum sin nombre',
  'albums.photoCount': ({ count }) => `${String(count)} ${plural(count, 'foto', 'fotos')}`,
  'albums.fromSailing': ' · de una salida',
  'albums.takenDown': ' · retirado',
  'albums.expires': ({ date }) => `hasta el ${date}`,
  'albums.titleLabel': 'Nombre del álbum',
  'albums.titlePlaceholder': 'Salida del atardecer, la familia Miller…',
  'albums.create': 'Crear un álbum',
  'albums.creating': 'Creando…',
  'albums.dialog': 'Álbum',
  'albums.addPhotos': 'Agregar fotos',
  'albums.addPhotosHint': ({ max }) =>
    `Hasta ${String(max)} a la vez. Se suben directamente desde este dispositivo.`,
  'albums.uploadsNeedConnection':
    'Para subir fotos hace falta conexión. Todo lo demás sigue funcionando sin señal — las fotos son lo único que no puede esperar en la cola.',
  'albums.uploadWaiting': 'en espera',
  'albums.uploadPercent': ({ percent }) => `${String(percent)} %`,
  'albums.uploadDone': 'lista',
  'albums.uploadFailed': 'falló — vuelva a enviarla',
  'albums.notifyPhone': 'Avisar a la familia',
  'albums.notifyPhonePlaceholder': 'su móvil — o en blanco para el cliente del álbum',
  'albums.notify': 'Enviar «tus fotos están listas»',
  'albums.notified': ({ phone }) =>
    `Enviado a ${phone}: el enlace a la página de fotos. El código sigue en la tarjeta.`,
  'albums.regenerate': 'Código nuevo',
  'albums.regenerateHint':
    'Un código nuevo deja de inmediato sin efecto la tarjeta anterior. Úselo si un código llegó a la familia equivocada.',
  'albums.codeDialog': 'El código del álbum',
  'albums.codeTitle': 'Anote esto en la tarjeta',
  'albums.codeOnce':
    'Esta es la única vez que se muestra el código. Guardamos una huella del código, no el código, así que no se puede volver a consultar — si se pierde, cree uno nuevo.',
  'albums.copy': 'Copiar',
  'albums.copied': 'Copiado',
  'albums.gotIt': 'Entendido',
  'albums.forSailing': 'Álbum',

  /* ---------------------------------------------------------------- */
  /* El conserje — el panel del dueño                                  */
  /*                                                                   */
  /* Los tokens de aiconcierge (`balanced`, `ai_first`) se imprimen    */
  /* tal cual, igual que los módulos: quien lea una palabra aquí y     */
  /* otra en una llamada de soporte tendría dos nombres para un mismo  */
  /* ajuste. Lo que se traduce es la frase que va al lado.             */
  /* ---------------------------------------------------------------- */

  'concierge.title': 'La recepción con IA',
  'concierge.note':
    'Contesta el teléfono y el chat. Lo que sabe — sus horarios, precios, políticas y preguntas — sale de esta app; lo que hace se define aquí.',
  'concierge.notConfigured':
    'La integración del conserje no está configurada en este despliegue. A su negocio no le falta nada — sencillamente aquí no hay ningún agente conectado.',
  'concierge.notEnabled':
    'La recepción con IA todavía no está encendida para este negocio. Pídaselo a quien le configuró la cuenta.',
  'concierge.unreachable': ({ message }) => `No se pudo contactar al agente ahora: ${message}`,
  'concierge.status': 'Estado',
  'concierge.knows': 'Conoce su',
  'concierge.menuVersion': ({ version, when }) => `menú (v${version}, leído ${when})`,
  'concierge.detailsVersion': ({ version, when }) => `información (v${version}, leída ${when})`,
  'concierge.neverSynced': 'todavía no ha leído nada',
  'concierge.missing': 'Pendiente',
  'concierge.missing.provider_connection':
    'El agente no puede leer su agenda. Pídale a quien configuró la cuenta que la vuelva a conectar.',
  'concierge.missing.catalog': 'Todavía no hay nada reservable publicado — agregue un servicio.',
  'concierge.missing.phone_number':
    'Todavía no hay número de teléfono: el chat funciona, el teléfono y los mensajes no.',
  'concierge.missing.owner_number':
    'No hay a quién escribirle cuando la necesite. Ponga su número abajo.',
  'concierge.openConsole': 'Abrir la consola del agente',

  'concierge.autonomy': 'Cuánto puede decidir',
  'concierge.autonomy.conservative':
    'Contesta y toma los datos pero no reserva nada: propone, y usted confirma.',
  'concierge.autonomy.balanced':
    'Reserva lo que esté en su menú y tenga permitido, y le consulta lo que sea a medida, con descuento o con molestia — con la respuesta ya redactada.',
  'concierge.autonomy.autonomous':
    'Reserva lo mismo sin consultar antes, y solo acude a usted por algo a medida o por encima del tope.',
  'concierge.ceiling': 'Consultar siempre por encima de (centavos)',
  'concierge.greeting': 'Primeras palabras en el chat',
  'concierge.voiceGreeting': 'Primeras palabras por teléfono',
  'concierge.voiceGreetingHint': 'Que no pase de unas doce palabras — se dice en voz alta.',
  'concierge.toneVoice': 'Cómo debe sonar, en una frase',
  'concierge.formality': 'Formalidad',
  'concierge.avoid': 'Palabras que nunca debe usar',
  'concierge.avoidHint': 'Una por línea.',
  'concierge.ownerNumber': 'Su número',
  'concierge.ownerNumberHint': 'Por ejemplo +13405550100. Ahí le escribe cuando la necesita.',
  'concierge.voiceMode': 'Por teléfono',
  'concierge.voiceMode.disabled': 'Sin teléfono — solo chat y mensajes.',
  'concierge.voiceMode.ai_first': 'Contesta el agente.',
  'concierge.voiceMode.owner_first': 'Primero le suena a usted y atiende lo que no alcance.',

  'concierge.services': 'Lo que puede reservar',
  'concierge.servicesNote': 'Un interruptor por cada servicio de su menú.',
  'concierge.servicesEmpty': 'Todavía no ha leído su menú — use Sincronizar ahora.',
  'concierge.notSynced': 'todavía sin sincronizar — use Sincronizar ahora',
  'concierge.mayBook': 'la IA puede reservar esto',

  'concierge.sync': 'Sincronizar ahora',
  'concierge.syncNote': 'Normalmente no hace falta — un cambio le llega en segundos.',
  'concierge.pause': 'Pausar',
  'concierge.resume': 'Volver a encenderla',
  'concierge.paused':
    'En pausa: sigue contestando y le toma un mensaje — solo que no reserva nada.',
  'concierge.save': 'Guardar',
  'concierge.saved': 'Guardado.',
  'concierge.nothingChanged': 'No cambió nada.',

  /* La estantería — reservas para recoger */

  'reservations.notSwitchedOn': 'Los productos no están activados para este negocio.',
  'reservations.title': 'Reservas',
  'reservations.couldNotReach': ' · no se pudo consultar la estantería ahora mismo',
  'reservations.reading': 'Leyendo la estantería…',
  'reservations.none': 'No hay nada esperando para recoger.',
  'reservations.someone': 'Alguien',
  'reservations.atPickup': 'al recoger',
  'reservations.qty': ({ qty }) => `${String(qty)}×`,
  'reservations.heldUntil': ({ date }) => `Guardado hasta el ${date}`,
  'reservations.lanePlaced': 'Por apartar',
  'reservations.laneReady': 'Listo para recoger',
  'reservations.laneCollected': 'Recogido',
  'reservations.laneClosed': 'Cerrados',
  'reservations.verbPlaced': 'Apartar',
  'reservations.verbReady': 'Marcar listo',
  'reservations.verbCollected': 'Recogido',
  'reservations.verbCancelled': 'Cancelar',
  'reservations.verbExpired': 'Ya no guardado',
  'reservations.ringUp': 'Cobrar',
  'reservations.ringUpHint': 'Abra Caja: estas líneas están esperando allí.',
  'reservations.tillNote':
    'Marcar una reserva como recogida registra que salió de la estantería. El dinero se cobra en la caja como cualquier otra venta.',

  /* ---------------------------------------------------------------- */
  /* El boletín (E3)                                                   */
  /* ---------------------------------------------------------------- */

  'newsletter.notSwitchedOn': 'La libreta de clientes no está activada para este negocio.',
  'newsletter.reading': 'Leyendo sus boletines…',
  'newsletter.couldNotReach':
    'No se pudo contactar con el servidor. Esta pantalla necesita conexión.',
  'newsletter.noneYet': 'Todavía no hay nada escrito. Toque «Escribir uno» para empezar.',
  'newsletter.write': 'Escribir uno',
  'newsletter.newSubject': 'Esto es lo nuevo',
  'newsletter.untitled': 'Sin título',
  'newsletter.subject': 'Asunto',
  'newsletter.preheader': 'Línea de vista previa',
  'newsletter.preheaderHint': 'La línea gris que sigue al asunto en la bandeja de entrada',
  'newsletter.saved': 'Guardado',
  'newsletter.preview': 'Vista previa',
  'newsletter.desktop': 'En un ordenador',
  'newsletter.phone': 'En un teléfono',
  'newsletter.sendNow': 'Enviar ahora',
  'newsletter.confirmSend': '¿Enviarlo a toda su lista? Esto no se puede deshacer.',
  'newsletter.testMe': ({ email }: { email: string }) => `Enviarme una prueba a ${email}`,
  'newsletter.testQueued': 'Le enviamos una prueba.',
  'newsletter.queued': ({ count }: { count: number }) =>
    `En camino a ${count} ${count === 1 ? 'persona' : 'personas'}.`,
  'newsletter.audience': ({ count }: { count: number }) =>
    count === 0
      ? 'Todavía no hay nadie en su lista.'
      : `${count} ${count === 1 ? 'persona esta' : 'personas están'} en su lista.`,
  'newsletter.from': ({ from }: { from: string }) => `Se envía desde ${from}`,
  'newsletter.replyTo': ({ to }: { to: string }) => `Las respuestas van a ${to}`,
  'newsletter.joinLink': 'Ponga este enlace en una tarjeta para que la gente se apunte:',
  'newsletter.counts': ({
    sent,
    failed,
    unsubscribed,
  }: {
    sent: number;
    failed: number;
    unsubscribed: number;
  }) => `${sent} enviados - ${failed} no llegaron - ${unsubscribed} se dieron de baja`,
  'newsletter.noTracking': 'No registramos quién lo abrió. Nadie vigila a sus clientes.',
  'newsletter.text': 'Palabras',
  'newsletter.textHint': 'Frases sencillas. *negrita*, _cursiva_, [un enlace](https://...)',
  'newsletter.imageUrl': 'Dirección de la foto',
  'newsletter.alt': 'Qué muestra la foto',
  'newsletter.altHint': 'Mucha gente ve esto en lugar de la foto',
  'newsletter.product': 'Qué producto',
  'newsletter.blurb': 'Su propia frase sobre él',
  'newsletter.priceFromCatalog':
    'El precio y la foto vienen de su tienda, asi que nunca se equivocan.',
  'newsletter.label': 'Texto del botón',
  'newsletter.linkUrl': 'Adónde lleva',
  'newsletter.block.heading': 'Título',
  'newsletter.block.text': 'Palabras',
  'newsletter.block.image': 'Foto',
  'newsletter.block.product': 'Producto',
  'newsletter.block.button': 'Botón',
  'newsletter.block.divider': 'Línea',
  'newsletter.status.draft': 'Borrador',
  'newsletter.status.scheduled': 'Programado',
  'newsletter.status.sending': 'Enviando',
  'newsletter.status.sent': 'Enviado',
  'newsletter.status.cancelled': 'Cancelado',
};
